import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Loader2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal' });

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    try { const res = await api.get('/admin/announcements'); setAnnouncements(res.data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createAnnouncement = async () => {
    if (!form.title || !form.content) { toast.error('Fill in all fields'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/announcements', form);
      toast.success('Announcement posted');
      setShowNew(false);
      setForm({ title: '', content: '', priority: 'normal' });
      loadAnnouncements();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const deleteAnnouncement = async (id) => {
    try { await api.delete(`/admin/announcements/${id}`); toast.success('Deleted'); loadAnnouncements(); }
    catch { toast.error('Failed to delete'); }
  };

  const priorityColor = (p) => ({ urgent: 'bg-red-50 text-red-700 border-red-100', normal: 'bg-slate-100 text-slate-600', important: 'bg-orange-50 text-orange-700 border-orange-100' }[p] || 'bg-slate-100 text-slate-600');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Announcements</h1>
          <p className="text-sm text-slate-500 mt-1">{announcements.length} notices posted</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="new-announcement-btn"><Plus size={14} /> New Announcement</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-['Outfit']">Post Announcement</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-xs mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Announcement title" data-testid="ann-title-input" /></div>
              <div><Label className="text-xs mb-1 block">Content</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Write your announcement..." rows={4} data-testid="ann-content-input" /></div>
              <div>
                <Label className="text-xs mb-1 block">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="ann-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="important">Important</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createAnnouncement} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-announcement-btn">
                {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Post Announcement
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {announcements.length === 0 && <p className="text-sm text-slate-400 text-center py-12">No announcements yet</p>}
        {announcements.map(a => (
          <Card key={a.announcement_id} className="border-slate-100 card-hover" data-testid={`ann-card-${a.announcement_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Megaphone size={18} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-slate-900 font-['Outfit']">{a.title}</h3>
                    <Badge className={`text-[10px] ${priorityColor(a.priority)}`}>{a.priority}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{a.content}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-xs text-slate-400">by {a.created_by_name || 'Admin'}</span>
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(a.announcement_id)} className="text-red-400 hover:text-red-600 h-8 w-8 p-0 flex-shrink-0" data-testid={`delete-ann-${a.announcement_id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
